
import { GoogleGenAI, Type } from "@google/genai";
import { MealSession, SymptomSession, Language, AnalysisResult, SuspectStat, ChemicalCluster, DeepInsight, DietaryProfile } from "../types";
import { getDecomposition } from "../database/decomposition-map";
import { foods } from "../database/food-database";

// Helper: Normalize ingredient name for matching
const normalize = (s: string) => s.trim().toLowerCase();

// Ignored ingredients for statistical correlation (Common non-triggers)
// Explicitly excluding Salt/Sel as per v0.2 requirements
const IGNORED_INGREDIENTS = new Set(['salt', 'sel', 'water', 'eau', 'ice', 'glace']);

/**
 * LEVEL 1: Incremental Correlation Algorithm (Local)
 * Score +1 if ingredient appears <12h before a symptom.
 * Score -0.5 if ingredient appears >12h before NO symptom.
 */
function runLevel1Analysis(meals: MealSession[], symptoms: SymptomSession[], language: Language): SuspectStat[] {
    const scores: Record<string, { score: number; freq: number; incidents: number }> = {};
    const REACTION_WINDOW_MS = 12 * 60 * 60 * 1000; // 12 hours

    // Helper to get base ingredients from a session
    const extractBaseIngredients = (session: MealSession): string[] => {
        const ingredients = new Set<string>();
        
        session.items.forEach(item => {
            // If it has explicit sub-ingredients (from recipe modal), use those
            if (item.ingredients && item.ingredients.length > 0) {
                item.ingredients.forEach(sub => ingredients.add(normalize(sub.name)));
            } else {
                // Otherwise check database decomposition
                const dbFood = foods.find(f => f[language].toLowerCase() === normalize(item.name));
                if (dbFood) {
                    const decomp = getDecomposition(dbFood.id);
                    if (decomp) {
                        decomp.forEach(d => ingredients.add(d)); // d is already normalized ID usually, but sticking to text for now
                    } else {
                        ingredients.add(normalize(item.name));
                    }
                } else {
                    ingredients.add(normalize(item.name));
                }
            }
        });
        
        // Filter out ignored ingredients (Salt, Water, etc.)
        return Array.from(ingredients).filter(ing => !IGNORED_INGREDIENTS.has(ing));
    };

    // 1. Process Meals
    meals.forEach(meal => {
        const mealTime = new Date(meal.timestamp).getTime();
        const mealIngs = extractBaseIngredients(meal);

        // Check if any symptom occurred within window AFTER this meal
        const subsequentSymptoms = symptoms.filter(s => {
            const symTime = new Date(s.timestamp).getTime();
            return symTime > mealTime && symTime < mealTime + REACTION_WINDOW_MS;
        });

        const hasReaction = subsequentSymptoms.length > 0;
        const severityWeight = hasReaction 
            ? Math.max(...subsequentSymptoms.flatMap(s => s.items.map(i => i.severity))) / 10 
            : 0;

        mealIngs.forEach(ing => {
            if (!scores[ing]) scores[ing] = { score: 0, freq: 0, incidents: 0 };
            scores[ing].freq += 1;

            if (hasReaction) {
                scores[ing].score += (1 + severityWeight); // Heavy penalty for severe reactions
                scores[ing].incidents += 1;
            } else {
                scores[ing].score -= 0.5; // Reward for safety, but conservative
            }
        });
    });

    // Convert to array and sort
    return Object.entries(scores)
        .map(([name, data]) => ({
            name: name,
            score: parseFloat(data.score.toFixed(2)),
            frequency: data.freq,
            incidentCount: data.incidents
        }))
        .filter(stat => stat.score > 0) // Only return positive correlations
        .sort((a, b) => b.score - a.score)
        .slice(0, 10); // Return top 10 for internal use, UI will show top 5
}

/**
 * LEVEL 2: Chemical Component Analysis (Gemini)
 * Takes Top 5 suspects -> Maps common chemistry.
 */
async function runLevel2Analysis(top5: SuspectStat[], language: Language, apiKey: string): Promise<ChemicalCluster[]> {
    if (top5.length < 2) return [];

    const ai = new GoogleGenAI({ apiKey });
    const ingredientsList = top5.map(s => s.name).join(", ");
    
    const prompt = language === 'fr'
        ? `Analyse ces 5 ingrédients : ${ingredientsList}. 
           Trouve les composants chimiques communs (ex: Histamine, FODMAPs, Salicylates, Tyramine, Lectines, Solanine, etc.) qui pourraient causer des symptômes.
           Groupe-les. Si "Tomate" et "Épinard" contiennent tous les deux de l'Histamine, crée un cluster "Histamine".
           IMPORTANT: La description DOIT être en Français.
           Réponds UNIQUEMENT en JSON.`
        : `Analyze these 5 ingredients: ${ingredientsList}.
           Find common chemical components (e.g., Histamine, FODMAPs, Salicylates, Tyramine, Lectins, Solanine, etc.) that are known triggers.
           Group them. If "Tomato" and "Spinach" both have Histamine, create a cluster "Histamine".
           Response Format: JSON only.`;

    const schema = {
        type: Type.ARRAY,
        items: {
            type: Type.OBJECT,
            properties: {
                chemicalName: { type: Type.STRING },
                description: { type: Type.STRING, description: "Short explanation of why this chemical causes symptoms." },
                foundInIngredients: { type: Type.ARRAY, items: { type: Type.STRING } },
                riskLevel: { type: Type.STRING, enum: ['high', 'medium', 'low'] }
            },
            required: ['chemicalName', 'description', 'foundInIngredients', 'riskLevel']
        }
    };

    try {
        const response = await ai.models.generateContent({
            model: 'gemini-3-flash-preview',
            contents: prompt,
            config: {
                responseMimeType: "application/json",
                responseSchema: schema
            }
        });
        return JSON.parse(response.text || "[]");
    } catch (e) {
        console.error("Level 2 Analysis Failed", e);
        return [];
    }
}

/**
 * LEVEL 3: Deep Medical Insight (Gemini)
 * Contextualizes everything.
 */
async function runLevel3Analysis(
    stats: SuspectStat[], 
    clusters: ChemicalCluster[], 
    symptoms: SymptomSession[],
    language: Language, 
    apiKey: string
): Promise<DeepInsight | null> {
    const ai = new GoogleGenAI({ apiKey });

    // Prepare context
    const recentSymptoms = symptoms.slice(0, 10).flatMap(s => s.items.map(i => i.symptom)).join(", ");
    const topSuspectsStr = stats.slice(0, 5).map(s => `${s.name} (Correlation: ${s.score})`).join("; ");
    const chemistryStr = clusters.map(c => `${c.chemicalName} found in ${c.foundInIngredients.join(', ')}`).join("; ");

    const prompt = language === 'fr'
        ? `Agis comme un médecin nutritionniste expert. Voici les données d'un patient :
           - Symptômes récents : ${recentSymptoms}
           - Aliments suspects (Corrélation statistique) : ${topSuspectsStr}
           - Liens chimiques identifiés : ${chemistryStr}
           
           Génère un rapport clinique court mais dense. Réponds impérativement en Français.
           1. Résumé : Une vue d'ensemble de la situation.
           2. Profil : Quel type de profil réactif semble se dessiner (ex: Intolérance Histamine, IBS, etc.) ?
           3. Recommandation : 1 conseil actionnable et précis.`
        : `Act as an expert clinical nutritionist. Here is patient data:
           - Recent Symptoms: ${recentSymptoms}
           - Suspect Foods (Statistical Correlation): ${topSuspectsStr}
           - Chemical Links Identified: ${chemistryStr}
           
           Generate a short but dense clinical report.
           1. Summary: Overview of the situation.
           2. Profile: What reactive profile is emerging (e.g. Histamine Intolerance, IBS, FODMAP)?
           3. Recommendation: 1 specific, actionable piece of advice.`;

    const schema = {
        type: Type.OBJECT,
        properties: {
            summary: { type: Type.STRING },
            dietaryType: { type: Type.STRING },
            recommendation: { type: Type.STRING }
        },
        required: ['summary', 'dietaryType', 'recommendation']
    };

    try {
        const response = await ai.models.generateContent({
            model: 'gemini-3-flash-preview',
            contents: prompt,
            config: {
                responseMimeType: "application/json",
                responseSchema: schema
            }
        });
        return JSON.parse(response.text || "{}");
    } catch (e) {
        console.error("Level 3 Analysis Failed", e);
        return null;
    }
}

// MAIN EXPORT
export async function runFullAnalysis(
    meals: MealSession[], 
    symptoms: SymptomSession[], 
    language: Language
): Promise<AnalysisResult> {
    const apiKey = process.env.API_KEY;
    
    // Level 1: Local
    const level1Stats = runLevel1Analysis(meals, symptoms, language);
    const top5 = level1Stats.slice(0, 5);

    let clusters: ChemicalCluster[] = [];
    let deepInsight: DeepInsight | null = null;

    if (apiKey && top5.length > 0) {
        // Level 2: Chemical
        clusters = await runLevel2Analysis(top5, language, apiKey);
        
        // Level 3: Deep
        deepInsight = await runLevel3Analysis(top5, clusters, symptoms, language, apiKey);
    }

    return {
        isReady: true,
        currentEntryCount: meals.length + symptoms.length,
        requiredEntryCount: 10, // lowered for demo
        daysTracked: new Set(meals.map(m => new Date(m.timestamp).toDateString())).size,
        requiredDays: 3, // lowered for demo
        topSuspects: level1Stats,
        chemicalClusters: clusters,
        deepInsight: deepInsight,
        lastAnalyzed: new Date()
    };
}

// Stub for legacy compat if needed, otherwise replaced by runFullAnalysis logic flow
export async function analyzeCorrelations(meals: MealSession[], symptoms: SymptomSession[], language: Language) {
    return []; 
}

export async function analyzeDietaryProfile(recentFoods: string[], language: Language): Promise<DietaryProfile | null> {
    const apiKey = process.env.API_KEY;
    if (!apiKey) return null;

    const ai = new GoogleGenAI({ apiKey });
    const foodList = recentFoods.slice(0, 50).join(", "); 

    const prompt = language === 'fr'
        ? `Analyse ce journal alimentaire récent : ${foodList}.
           Crée un profil diététique concis.
           Réponds UNIQUEMENT en JSON avec les clés :
           - dietType (ex: "Riche en glucides", "Équilibré", "Keto", etc.)
           - explanation (court résumé en français)
           - macros (estimation approximative %: protein, carbs, fats, fiber en g)
           - keyNutrients (liste de 3-4 nutriments clés présents)
           - categoryCounts (compte approximatif : grains, proteins, dairy, vegetables, fruits, fats, water)`
        : `Analyze this recent food log: ${foodList}.
           Create a concise dietary profile.
           Respond ONLY in JSON with keys:
           - dietType (e.g. "Carb Heavy", "Balanced", "Keto")
           - explanation (short summary)
           - macros (approx %: protein, carbs, fats, fiber in g)
           - keyNutrients (list of 3-4 key nutrients present)
           - categoryCounts (approx count: grains, proteins, dairy, vegetables, fruits, fats, water)`;

    const schema = {
        type: Type.OBJECT,
        properties: {
            dietType: { type: Type.STRING },
            explanation: { type: Type.STRING },
            macros: {
                type: Type.OBJECT,
                properties: {
                    protein: { type: Type.NUMBER },
                    carbs: { type: Type.NUMBER },
                    fats: { type: Type.NUMBER },
                    fiber: { type: Type.NUMBER }
                }
            },
            keyNutrients: { type: Type.ARRAY, items: { type: Type.STRING } },
            categoryCounts: {
                type: Type.OBJECT,
                properties: {
                    grains: { type: Type.NUMBER },
                    proteins: { type: Type.NUMBER },
                    dairy: { type: Type.NUMBER },
                    vegetables: { type: Type.NUMBER },
                    fruits: { type: Type.NUMBER },
                    fats: { type: Type.NUMBER },
                    water: { type: Type.NUMBER }
                }
            }
        },
        required: ['dietType', 'explanation', 'macros', 'keyNutrients', 'categoryCounts']
    };

    try {
        const response = await ai.models.generateContent({
            model: 'gemini-3-flash-preview',
            contents: prompt,
            config: {
                responseMimeType: "application/json",
                responseSchema: schema
            }
        });
        return JSON.parse(response.text || "{}");
    } catch (e) {
        console.error("Dietary Profile Analysis Failed", e);
        return null;
    }
}
